import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import config from "../../config";

const sections = [
  {
    id: "understanding",
    heading: "Understanding Neurodiversity in Speech Therapy",
    body: [
      `Speech-Language Pathology has traditionally focused on normative communication milestones — measuring how closely a child's speech, language, and social interaction align with neurotypical expectations. However, the neurodiversity movement has brought a transformative paradigm shift to the field.`,
      `Neurodiversity-affirming speech therapy recognizes that variation in neurological development is a natural and valuable part of human diversity. Rather than viewing autistic or neurodivergent communication styles as deficits to be corrected, affirming speech therapy seeks to understand, support, and validate each individual's unique way of connecting with the world.`,
      `This approach shifts the core objective of speech therapy from compliance and normative speech production to meaningful, self-directed, and authentic communication.`
    ]
  },
  {
    id: "principles",
    heading: "Core Principles of Neurodiversity-Affirming Practice",
    body: [
      `1. Presuming Competence: We operate under the foundational belief that every individual has the capacity to understand, learn, and express thoughts, regardless of whether they speak verbally.`,
      `2. Honoring All Forms of Communication: Communication extends far beyond spoken words. Echolalia, scripting, gestures, facial expressions, body language, and Augmentative and Alternative Communication (AAC) devices are all valid and meaningful communication channels.`,
      `3. Autonomy and Connection over Compliance: Traditional goals that forced eye contact, suppressed natural self-regulation (stimming), or insisted on quiet hands are replaced by goals focused on self-advocacy, emotional regulation, and genuine connection.`,
      `4. Child-Led & Play-Based Interaction: Therapy follows the child's natural interests and intrinsic motivation. When we join a child in their world, communication unfolds naturally and joyfully.`
    ]
  },
  {
    id: "impact",
    heading: "The Real-World Impact on Children and Families",
    body: [
      `When speech therapy embraces neurodiversity, the positive impact on a child's well-being and confidence is profound. Children who are accepted for who they are develop stronger self-esteem, reduced anxiety, and a safer space to explore communication.`,
      `For families, this approach replaces the stress of constant performance with collaborative partnership. Parents learn to interpret their child's unique communication signals, celebrate spontaneous self-expression, and advocate for inclusive environments in school and community settings.`
    ]
  },
  {
    id: "future",
    heading: "Moving Forward Together at Ray of Hope",
    body: [
      `At Ray of Hope Center for Autism, our Speech and Language Pathology team is dedicated to providing evidence-informed, neurodiversity-affirming care. We tune into regulation needs, honor every communication mode, and partner deeply with families.`,
      `Communication is a fundamental human right. By embracing neurodiversity in speech therapy, we ensure every voice — spoken, typed, signed, or gestured — is heard, valued, and celebrated.`
    ]
  }
];

export default function SpeechNeurodiversityBlogPost() {
  const animRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    animRefs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  const ref = (el) => {
    if (el && !animRefs.current.includes(el)) animRefs.current.push(el);
  };

  return (
    <div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');

        .speech-blog-wrap {
          background-color: #F8FAF7;
          color: #1F2937;
          font-family: 'Plus Jakarta Sans', sans-serif;
          line-height: 1.8;
          padding-bottom: 4rem;
        }

        .speech-blog-top-band {
          background: #15803D;
          padding: 0.85rem clamp(1.5rem, 6vw, 8rem);
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .speech-blog-top-band .tag {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: #DCFCE7;
        }
        .speech-blog-top-band .dot { color: #86EFAC; font-size: 0.5rem; }
        .speech-blog-top-band .org { font-size: 0.8rem; color: rgba(255,255,255,0.85); }

        .speech-hero {
          padding: 4rem clamp(1.5rem, 6vw, 8rem) 2rem;
          max-width: 1100px;
          margin: 0 auto;
        }
        .speech-hero-eyebrow {
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #16A34A;
          margin-bottom: 0.75rem;
        }
        .speech-hero h1 {
          font-family: 'Outfit', sans-serif;
          font-size: clamp(2rem, 4vw, 3.2rem);
          font-weight: 700;
          color: #111827;
          line-height: 1.25;
          margin-bottom: 1.25rem;
        }
        .speech-hero-byline {
          font-size: 0.95rem;
          color: #4B5563;
        }

        .speech-content-card {
          max-width: 900px;
          margin: 0 auto 3rem;
          background: #FFFFFF;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          padding: clamp(2rem, 5vw, 3.5rem);
        }

        .speech-section {
          margin-bottom: 2.5rem;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .speech-section.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .speech-section h2 {
          font-family: 'Outfit', sans-serif;
          font-size: 1.6rem;
          font-weight: 700;
          color: #166534;
          margin-bottom: 1rem;
          border-bottom: 2px solid #DCFCE7;
          padding-bottom: 0.5rem;
        }

        .speech-section p {
          font-size: 1.05rem;
          color: #374151;
          margin-bottom: 1rem;
        }

        .speech-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.75rem;
          background-color: #166534;
          color: #FFFFFF !important;
          border-radius: 50px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
        }
        .speech-back-btn:hover {
          background-color: #15803D;
          transform: translateX(-3px);
        }
      `}</style>

      <div className="speech-blog-wrap">
        <section className="page-header">
          <div 
            className="page-header-bg" 
            style={{ backgroundImage: `url(${config.baseUrl}/assets/images/backgrounds/blogpage-Header-1894-434.jpg)` }}
          ></div>
          <div className="container">
            <div className="page-header__inner">
              <h2>Speech Therapy Insights</h2>
              <ul className="thm-breadcrumb list-unstyled">
                <li><Link to="/">Home</Link></li>
                <li><span>/</span></li>
                <li><Link to="/blogs">Blogs</Link></li>
                <li><span>/</span></li>
                <li><span>Speech Therapy</span></li>
              </ul>
            </div>
          </div>
        </section>

        <div className="speech-blog-top-band">
          <span className="tag">Speech Therapy &amp; Neurodiversity</span>
          <span className="dot">●</span>
          <span className="org">Ray of Hope Center for Autism</span>
        </div>

        <header className="speech-hero">
          <p className="speech-hero-eyebrow">Department Insights · Speech Language Pathology</p>
          <h1>Embracing Neurodiversity in Speech Therapy: Understanding the Field and Its Impact</h1>
          <p className="speech-hero-byline">
            Written by <strong>RHCA Speech Therapy Team</strong> · Speech-Language Pathology Department
          </p>
        </header>

        <main className="container">
          <div className="speech-content-card">
            {sections.map((section) => (
              <div key={section.id} id={section.id} className="speech-section" ref={ref}>
                <h2>{section.heading}</h2>
                {section.body.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            ))}

            <div className="text-center mt-5">
              <Link to="/blogs" className="speech-back-btn">
                <i className="fas fa-arrow-left"></i> Back to All Blogs
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
